"use client";

import React, {
    useState,
    useEffect,
    ReactNode,
    FunctionComponent,
    Children,
    isValidElement,
    cloneElement,
    RefCallback,
    useRef,
    KeyboardEvent,
} from "react";
import { useLayer } from "react-laag";
import { AnimatePresence } from "framer-motion";
import { PlacementType } from "react-laag/dist/PlacementType";
import { Arrow, Body, Wrapper, Close as CloseButton } from "./popover.styles";
// eslint-disable-next-line import/no-cycle
import { Button } from "../button/button";
import { More } from "../icons/more";
import { useMediaQuery } from "../hooks/responsive";
import { Close } from "../close/close";

export interface PopoverProps {
    /**
     * Where the popover will be opend.
     */
    placement?: PlacementType;

    /**
     * Whether the popover should be opened by default or not.
     */
    open?: boolean;

    /**
     * By default a button will be rendered for open/close the popover. Here you can override the button or use a custom component.
     */
    button?: ReactNode;

    /**
     * A callback function which will be fired on toggle the popover.
     */
    onToggle?: (open: boolean) => void;

    /**
     * The popover cannot be closed.
     */
    keepOpen?: boolean;

    /**
     * Enable keybord navigation.
     */
    enableKeyboardNavigation?: boolean;

    /**
     * Display the popover full screen until a specific viewport width.
     */
    fullscreenUntil?: number;

    /**
     * If you want to render belonging list items, this prop will adjust the styling to that.
     */
    list?: boolean;

    /**
     * Size of the popover.
     */
    size?: "sm" | "md" | "lg";

    /**
     * Children of the popover.
     */
    children?: ReactNode;

    /**
     * If you want to render a datepicker, this prop will adjust the styling.
     */
    datepicker?: boolean;

    /**
     * If you want to render a dateTimepicker, this prop will adjust the styling.
     */
    dateTimepicker?: boolean;

    /**
     * Class name of the popover.
     */
    className?: string;
}

interface ChildrenProps {
    ref: RefCallback<HTMLDivElement>;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    active: boolean;
}

export const Popover: FunctionComponent<PopoverProps> = ({
    children,
    placement,
    open = false,
    button,
    keepOpen,
    enableKeyboardNavigation = true,
    fullscreenUntil,
    datepicker,
    onToggle,
    list = true,
    size,
    className,
}) => {
    const useFullscreenMode = useMediaQuery(`(max-width: ${fullscreenUntil || 0}px)`);
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const [loaded, setLoaded] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [lastClickedButton, setLastClickedButton] = useState<HTMLElement | null>(null);
    const firstFocusableElementRef = useRef<HTMLElement | null>(null);
    const lastFocusableElementRef = useRef<HTMLElement | null>(null);
    const { renderLayer, triggerProps, layerProps, arrowProps } = useLayer({
        isOpen,
        onDisappear: () => toggle(false),
        overflowContainer: true,
        auto: true,
        placement: useFullscreenMode ? "center" : placement || "bottom-end",
        triggerOffset: 12,
        containerOffset: 16,
        arrowOffset: 16,
        onOutsideClick: clickOutsideHandler,
    });
    const toggleButton = Children.map(button, (child) => {
        if (isValidElement(child)) {
            return cloneElement(child as React.ReactElement<ChildrenProps>, {
                ref: triggerProps.ref,
                onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
                    event.preventDefault();
                    event.stopPropagation();
                    toggle(!isOpen);
                },
                active: isOpen ? true : false,
            });
        }
        return child;
    });

    useEffect(() => {
        setLoaded(true);
        const buttons = document.querySelectorAll("button, [href]");

        buttons.forEach((button) => {
            button.addEventListener("click", handleButtonClick);
        });

        return function cleanup() {
            buttons.forEach((button) => {
                button.removeEventListener("click", handleButtonClick);
            });
        };
    }, []);

    useEffect(() => {
        setIsOpen(open);
    }, [open]);

    function toggle(open: boolean): void {
        onToggle?.(open);
        if (!keepOpen) {
            setIsOpen(open);
        }
        if (open) {
            setTimeout(function () {
                if (bodyRef.current && enableKeyboardNavigation) {
                    bodyRef.current.focus();
                    const focusableElements = bodyRef.current.querySelectorAll(
                        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
                    );
                    if (focusableElements.length > 0) {
                        const firstElement = focusableElements[0];
                        const lastElement = focusableElements[focusableElements.length - 1];
                        if (firstElement instanceof HTMLElement && lastElement instanceof HTMLElement) {
                            firstFocusableElementRef.current = firstElement;
                            lastFocusableElementRef.current = lastElement;
                        }
                    }
                }
            }, 10);
        }
    }

    function handleButtonClick(event: any) {
        if (!open && !loaded) {
            setLastClickedButton(event.currentTarget as HTMLButtonElement | null);
        }
    }

    function clickOutsideHandler(): void {
        if (!document.querySelector(".select-menu") && !keepOpen) {
            toggle(false);
        }
    }

    function handleKeyDown(event: KeyboardEvent<HTMLElement>) {
        if (enableKeyboardNavigation && bodyRef.current) {
            const focusableElements = bodyRef.current.querySelectorAll<HTMLElement>(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
            );
            const activeElement = document.activeElement;
            const indexFocusedElement = activeElement ? Array.from(focusableElements).indexOf(activeElement as HTMLElement) : -1;
            if (
                ((event.key === "Tab" && !event.shiftKey) || event.key === "ArrowDown") &&
                firstFocusableElementRef.current &&
                activeElement === lastFocusableElementRef.current
            ) {
                event.preventDefault();
                firstFocusableElementRef.current.focus();
            } else if (
                ((event.key === "Tab" && event.shiftKey) || event.key == "ArrowUp") &&
                lastFocusableElementRef.current &&
                activeElement === firstFocusableElementRef.current
            ) {
                event.preventDefault();
                lastFocusableElementRef.current.focus();
            } else if (event.key === "Escape") {
                setIsOpen(false);
                if (lastClickedButton) {
                    lastClickedButton.focus();
                }
                event.preventDefault();
            } else {
                if (event.key === "ArrowDown") {
                    const nextElement = focusableElements[indexFocusedElement + 1]
                        ? focusableElements[indexFocusedElement + 1]
                        : focusableElements[0];
                    if (nextElement) {
                        nextElement.focus();
                    }
                    event.preventDefault();
                } else if (event.key === "ArrowUp") {
                    const prevElement = focusableElements[indexFocusedElement - 1]
                        ? focusableElements[indexFocusedElement - 1]
                        : focusableElements[focusableElements.length - 1];
                    if (prevElement) {
                        prevElement.focus();
                    }
                    event.preventDefault();
                }
            }
        }
    }

    const enhancedChildren =
        children &&
        Children.map(children, (child) => {
            if (isValidElement(child)) {
                return cloneElement(child as any, { isOpen, setIsOpen });
            }
            return child;
        });

    return (
        <>
            {button ? (
                <>{toggleButton}</>
            ) : (
                <Button
                    color="white"
                    shadow={false}
                    rounded="md"
                    size="sm"
                    leftAdornments={<More />}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        toggle(!isOpen);
                    }}
                    ref={triggerProps.ref}
                />
            )}
            {renderLayer(
                <AnimatePresence>
                    {isOpen && (
                        <Wrapper
                            list={list}
                            datepicker={datepicker}
                            useFullscreenMode={useFullscreenMode}
                            size={size}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.18 }}
                            className={className}
                            {...layerProps}
                            style={useFullscreenMode ? { position: "fixed", inset: 0 } : layerProps.style}
                        >
                            <Body onKeyDown={handleKeyDown} ref={bodyRef} tabIndex={0}>
                                {useFullscreenMode && (
                                    <CloseButton>
                                        <Close color="gray" onClick={() => toggle(!isOpen)} />
                                    </CloseButton>
                                )}
                                {enhancedChildren}
                            </Body>
                            <Arrow
                                {...arrowProps}
                                backgroundColor="white"
                                borderWidth={1}
                                roundness={2}
                                borderColor="#e3e6ea"
                                size={8}
                                onPointerEnterCapture={undefined}
                                onPointerLeaveCapture={undefined}
                            />
                        </Wrapper>
                    )}
                </AnimatePresence>,
            )}
        </>
    );
};
